import {
  forwardRef,
  useEffect,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import clsx from 'clsx';
import { Loader2, X } from 'lucide-react';

/**
 * The shared UI primitives. Everything here is sized for touch first: the
 * smallest interactive target is 40px tall, the default is 48px.
 */

/* --------------------------------- button -------------------------------- */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-white font-medium border border-accent hover:bg-accent-strong hover:border-accent-strong active:bg-accent-strong',
  secondary:
    'bg-surface text-ink border border-line-strong hover:bg-hover active:bg-line',
  ghost: 'bg-transparent text-ink-soft border border-transparent hover:bg-hover hover:text-ink',
  danger: 'bg-surface text-danger border border-danger/35 hover:bg-danger/8 active:bg-danger/15',
  success: 'bg-surface text-positive border border-positive/35 hover:bg-positive/8 active:bg-positive/15',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-10 px-3 text-sm rounded-md gap-1.5',
  md: 'h-12 px-4 text-sm rounded-lg gap-2',
  lg: 'h-14 px-6 text-base rounded-lg gap-2.5',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, block, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center whitespace-nowrap transition-colors duration-150',
        'disabled:cursor-not-allowed disabled:opacity-40',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : icon}
      {children}
    </button>
  );
});

/* ---------------------------------- card --------------------------------- */

export function Card({
  className,
  children,
  padded = true,
}: {
  className?: string;
  children: ReactNode;
  padded?: boolean;
}) {
  return (
    <div
      className={clsx('rounded-card border border-line bg-surface shadow-sm', padded && 'p-5', className)}
    >
      {children}
    </div>
  );
}

export function SectionHeading({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-5 flex items-end justify-between gap-4">
      <div>
        <h2 className="font-display text-2xl leading-tight tracking-wide text-ink">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-ink-faint">{subtitle}</p> : null}
      </div>
      {action}
    </div>
  );
}

/* --------------------------------- badge --------------------------------- */

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'accent' | 'positive' | 'caution' | 'danger' | 'info';
  className?: string;
}) {
  const tones = {
    neutral: 'border-line-strong bg-raised text-ink-soft',
    accent: 'border-accent/30 bg-accent/10 text-accent-strong',
    positive: 'border-positive/30 bg-positive/10 text-positive',
    caution: 'border-caution/30 bg-caution/10 text-caution',
    danger: 'border-danger/30 bg-danger/10 text-danger',
    info: 'border-reserved/30 bg-reserved/10 text-reserved',
  } as const;

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/* --------------------------------- fields -------------------------------- */

const FIELD_BASE =
  'w-full rounded-lg border border-line-strong bg-surface px-3.5 text-ink placeholder:text-ink-faint ' +
  'transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none ' +
  'disabled:bg-raised disabled:opacity-60';

export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={clsx('block', className)}>
      {label ? (
        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-faint">
          {label}
        </span>
      ) : null}
      {children}
      {error ? <span className="mt-1 block text-xs text-danger">{error}</span> : null}
      {!error && hint ? <span className="mt-1 block text-xs text-ink-faint">{hint}</span> : null}
    </label>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={clsx(FIELD_BASE, 'h-12', className)} {...rest} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={clsx(FIELD_BASE, 'py-3', className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={clsx(FIELD_BASE, 'h-12 pr-8', className)} {...rest}>
        {children}
      </select>
    );
  },
);

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex items-center gap-3 text-sm text-ink"
    >
      <span
        className={clsx(
          'relative h-6 w-11 shrink-0 rounded-full border transition-colors',
          checked ? 'border-accent bg-accent' : 'border-line-strong bg-line',
        )}
      >
        <span
          className={clsx(
            'absolute top-0.5 left-0.5 h-4.5 w-4.5 rounded-full bg-surface shadow-sm transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0',
          )}
        />
      </span>
      {label}
    </button>
  );
}

/* --------------------------------- modal --------------------------------- */

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-3xl', xl: 'max-w-5xl' } as const;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-scrim/45 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={clsx(
          'relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-xl',
          widths[size],
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-line px-6 py-5">
          <div>
            <h2 className="font-display text-2xl leading-tight tracking-wide text-ink">{title}</h2>
            {subtitle ? <p className="mt-1 text-sm text-ink-faint">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-1 rounded-lg p-2 text-ink-faint transition hover:bg-hover hover:text-ink"
          >
            <X size={20} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer ? (
          <footer className="flex items-center justify-end gap-3 border-t border-line bg-raised px-6 py-4">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------ confirmation ----------------------------- */

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  tone = 'danger',
  loading,
  onConfirm,
  onCancel,
  children,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  tone?: 'danger' | 'primary';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-ink-soft">{message}</p>
      {children ? <div className="mt-4">{children}</div> : null}
    </Modal>
  );
}

/* ------------------------------ screen lock ------------------------------ */

/**
 * A full-screen lock shown while a write is in flight. It sits above the modal
 * layer (z-50) and swallows pointer events and key presses, so a waiter who
 * taps twice on a slow connection cannot send the same item to the kitchen
 * twice while the round trip to the database is still open.
 */
export function BusyOverlay({ open, label }: { open: boolean; label?: string }) {
  useEffect(() => {
    if (!open) return;
    const swallow = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };
    // Capture phase: Modal listens for Escape on window too, and must not hear it.
    window.addEventListener('keydown', swallow, true);
    return () => window.removeEventListener('keydown', swallow, true);
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="alertdialog"
      aria-busy="true"
      aria-live="assertive"
      aria-label={label ?? 'Working'}
      className="fixed inset-0 z-[60] flex cursor-wait items-center justify-center bg-scrim/55 backdrop-blur-[2px]"
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="flex items-center gap-3 rounded-xl border border-line bg-surface px-6 py-5 shadow-xl">
        <Loader2 className="animate-spin text-accent" size={22} />
        <span className="text-sm text-ink">{label ?? 'Working…'}</span>
      </div>
    </div>
  );
}

/* ---------------------------- status utilities --------------------------- */

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-12 text-ink-faint">
      <Loader2 className="animate-spin" size={20} />
      {label ? <span className="text-sm">{label}</span> : null}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  message,
  action,
}: {
  icon?: ReactNode;
  title: string;
  message?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-card border border-dashed border-line px-6 py-14 text-center">
      {icon ? <div className="text-ink-faint">{icon}</div> : null}
      <h3 className="font-display text-xl text-ink">{title}</h3>
      {message ? <p className="max-w-sm text-sm text-ink-faint">{message}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div className="rounded-card border border-danger/30 bg-danger/8 px-5 py-4">
      <p className="text-sm font-medium text-danger">{message}</p>
      {onRetry ? (
        <Button size="sm" variant="ghost" className="mt-3" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

/** Big, readable numeric stat used in headers and summary bars. */
export function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  tone?: 'default' | 'accent';
}) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-widest text-ink-faint">
        {label}
      </div>
      <div
        className={clsx(
          'tnum mt-0.5 font-display text-2xl leading-none',
          tone === 'accent' ? 'text-accent-strong' : 'text-ink',
        )}
      >
        {value}
      </div>
    </div>
  );
}
